/**
 * Egyptian Domino rules engine — board, turns, endings, scoring, projection.
 * Pure, deterministic, no I/O. See egyptian-domino-build-guide.md §2, §5.
 */

import type {
  ApplyResult,
  BonusLine,
  Chain,
  ChainPlacement,
  End,
  GameEvent,
  LegalMove,
  PlayerView,
  RoundResult,
  RoundState,
  RulesSnapshot,
  Tile,
} from "@domino/types";
import { opposingSides, seatsForSide, teamForSeat } from "@domino/types";

import { dealTiles } from "./deal";
import { shuffle } from "./rng";
import { buildTileSet, pipValueOf } from "./tiles";

const TILE_SET: readonly Tile[] = buildTileSet();

/** Tile metadata by id (ids are stable: a outer 0..6, b a..6 inner). */
export function getTile(tileId: number): Tile {
  const tile = TILE_SET[tileId];
  if (!tile) throw new Error(`Unknown tile id: ${tileId}`);
  return tile;
}

function halves(tileId: number): [number, number] {
  const t = getTile(tileId);
  return [t.high, t.low];
}

/** Number of scoring sides: team count in 2v2, else seat count. */
export function sideCount(rules: RulesSnapshot): number {
  return rules.mode === "3p" ? 3 : 2;
}

// ---------------------------------------------------------------------------
// Chain
// ---------------------------------------------------------------------------

export function emptyChain(): Chain {
  return { placements: [], leftEnd: null, rightEnd: null };
}

/** Value facing left / right out of a placement (junctions compare these). */
function leftValue(p: ChainPlacement): number {
  if (p.side === "center") return p.inwardHalf;
  return p.side === "left" ? p.outwardHalf : p.inwardHalf;
}

function rightValue(p: ChainPlacement): number {
  if (p.side === "center") return p.outwardHalf;
  return p.side === "right" ? p.outwardHalf : p.inwardHalf;
}

/** End values derived purely from the ordered placements. */
export function deriveEnds(chain: Chain): { left: number | null; right: number | null } {
  const ps = chain.placements;
  if (ps.length === 0) return { left: null, right: null };
  return { left: leftValue(ps[0]), right: rightValue(ps[ps.length - 1]) };
}

/**
 * Play a tile on an open end, returning a NEW chain.
 * A double leaves the exposed end number unchanged (R-10).
 */
export function applyPlayToChain(chain: Chain, tileId: number, end: End): Chain {
  const [high, low] = halves(tileId);
  const isDouble = high === low;

  if (chain.placements.length === 0) {
    return {
      placements: [{ tileId, side: "center", inwardHalf: high, outwardHalf: low }],
      leftEnd: high,
      rightEnd: low,
    };
  }

  if (end === "center") throw new Error("End required: the chain is not empty");

  const endValue = end === "left" ? chain.leftEnd : chain.rightEnd;
  if (endValue === null) throw new Error(`The ${end} end is not open`);

  let inwardHalf: number;
  let outwardHalf: number;
  if (high === endValue) {
    inwardHalf = high;
    outwardHalf = low;
  } else if (low === endValue) {
    inwardHalf = low;
    outwardHalf = high;
  } else {
    throw new Error(`Tile ${tileId} (${high}-${low}) does not match the ${end} end (${endValue})`);
  }

  const placement: ChainPlacement = { tileId, side: end, inwardHalf, outwardHalf };
  const placements =
    end === "left" ? [placement, ...chain.placements] : [...chain.placements, placement];

  return {
    placements,
    leftEnd: end === "left" ? (isDouble ? endValue : outwardHalf) : chain.leftEnd,
    rightEnd: end === "right" ? (isDouble ? endValue : outwardHalf) : chain.rightEnd,
  };
}

export function chainLength(chain: Chain): number {
  return chain.placements.length;
}

/** Throws when junctions do not match, or stored ends disagree with derived. */
export function validateChain(chain: Chain): void {
  const ps = chain.placements;
  for (let i = 0; i < ps.length - 1; i++) {
    const a = rightValue(ps[i]);
    const b = leftValue(ps[i + 1]);
    if (a !== b) {
      throw new Error(`Chain junction mismatch between placements ${i} and ${i + 1}: ${a} vs ${b}`);
    }
  }
  const derived = deriveEnds(chain);
  if (derived.left !== chain.leftEnd || derived.right !== chain.rightEnd) {
    throw new Error(
      `Chain ends mismatch: stored (${chain.leftEnd}, ${chain.rightEnd}) derived (${derived.left}, ${derived.right})`,
    );
  }
}

// ---------------------------------------------------------------------------
// Legal moves and turn order
// ---------------------------------------------------------------------------

/** Ends a specific tile may be played on (empty when it matches none). */
export function listLegalEnds(chain: Chain, tileId: number): End[] {
  const [high, low] = halves(tileId);
  if (chain.placements.length === 0) return ["center"];
  const ends: End[] = [];
  if (chain.leftEnd !== null && (high === chain.leftEnd || low === chain.leftEnd)) ends.push("left");
  if (chain.rightEnd !== null && (high === chain.rightEnd || low === chain.rightEnd)) {
    ends.push("right");
  }
  return ends;
}

/** One entry per playable hand tile, listing the ends it may be played on. */
export function listLegalMoves(hand: readonly number[], chain: Chain): LegalMove[] {
  const moves: LegalMove[] = [];
  for (const tileId of hand) {
    const ends = listLegalEnds(chain, tileId);
    if (ends.length > 0) moves.push({ tileId, ends });
  }
  return moves;
}

export function hasAnyLegalMove(hand: readonly number[], chain: Chain): boolean {
  return listLegalMoves(hand, chain).length > 0;
}

/**
 * Turn order. D-01 default is counter-clockwise, which here passes the turn to
 * the previous seat index; clockwise passes to the next. Always use this
 * helper — never assume index + 1.
 */
export function nextSeat(rules: RulesSnapshot, seat: number): number {
  const n = rules.seatCount;
  const step = rules.playDirection === "counter-clockwise" ? -1 : 1;
  return (((seat + step) % n) + n) % n;
}

/** The highest double in a hand, or null. */
export function highestDoubleIn(hand: readonly number[]): number | null {
  let best: number | null = null;
  for (const id of hand) {
    const t = getTile(id);
    if (!t.isDouble) continue;
    if (best === null || t.high > getTile(best).high) best = id;
  }
  return best;
}

/** Heaviest tile by pip total; ties broken by the higher single number. */
export function heaviestTileIn(hand: readonly number[]): number | null {
  let best: number | null = null;
  for (const id of hand) {
    const t = getTile(id);
    if (best === null) {
      best = id;
      continue;
    }
    const b = getTile(best);
    if (t.pipValue > b.pipValue || (t.pipValue === b.pipValue && t.high > b.high)) best = id;
  }
  return best;
}

/**
 * Legal moves for the opening play.
 *
 * House rule (ADR-0019): EVERY round is opened by the highest double's
 * holder LEADING that double — in 2v2 all 28 tiles are dealt so the 6-6 is
 * always in someone's hand; in draw modes the highest double among the dealt
 * hands opens. (Rare case: no doubles dealt at all — then any tile may open.)
 * `isRoundOne` is kept for call compatibility but no longer gates anything.
 */
export function openingLegalMoves(
  hand: readonly number[],
  rules: RulesSnapshot,
  isRoundOne = true,
): LegalMove[] {
  void isRoundOne;
  if (rules.openerMustPlayHighestDouble) {
    const double = highestDoubleIn(hand);
    if (double !== null) return [{ tileId: double, ends: ["center"] }];
  }
  return listLegalMoves(hand, emptyChain());
}

// ---------------------------------------------------------------------------
// Opener resolution (§2.4)
// ---------------------------------------------------------------------------

/** True when tile a is heavier than tile b (pip total, then higher single). */
function heavierThan(a: number, b: number): boolean {
  const ta = getTile(a);
  const tb = getTile(b);
  if (ta.pipValue !== tb.pipValue) return ta.pipValue > tb.pipValue;
  return ta.high > tb.high;
}

/** Round 1 opener: 6-6 holder, else the highest double, else the heaviest tile. */
export function resolveOpener(hands: readonly number[][], rules: RulesSnapshot): number {
  if (hands.length !== rules.seatCount) {
    throw new Error(`Expected ${rules.seatCount} hands, got ${hands.length}`);
  }

  // Case A: the 6-6 holder (always present in 2v2, where all 28 tiles are dealt).
  const sixSix = hands.findIndex((h) => h.includes(27));
  if (sixSix >= 0) return sixSix;

  // Case B: the holder of the HIGHEST double across all hands.
  let bestDouble: number | null = null;
  let doubleSeat = -1;
  hands.forEach((hand, seat) => {
    const d = highestDoubleIn(hand);
    if (d === null) return;
    if (bestDouble === null || getTile(d).high > getTile(bestDouble).high) {
      bestDouble = d;
      doubleSeat = seat;
    }
  });
  if (bestDouble !== null) return doubleSeat;

  // Case C: the holder of the heaviest tile; ties broken by higher single
  // number, then by the lowest seat index (deterministic).
  let best: number | null = null;
  let bestSeat = -1;
  hands.forEach((hand, seat) => {
    for (const id of hand) {
      if (best === null || heavierThan(id, best)) {
        best = id;
        bestSeat = seat;
      }
    }
  });
  if (best === null) throw new Error("No tile found to open with");
  return bestSeat;
}

/** Round 2+ opener (§2.4). */
export function computeNextOpener(input: {
  rules: RulesSnapshot;
  previousOpener: number;
  endReason: "domino" | "block" | "forfeit";
  winnerSeats: readonly number[];
  pipCounts: readonly number[];
  blockingSeat?: number;
}): number {
  const { rules, previousOpener, endReason, winnerSeats, pipCounts, blockingSeat } = input;

  if (endReason === "domino" && winnerSeats.length > 0) return winnerSeats[0];
  if (winnerSeats.length === 0) return nextSeat(rules, previousOpener); // tied block rotates

  const lowest = Math.min(...winnerSeats.map((s) => pipCounts[s] ?? Number.MAX_SAFE_INTEGER));
  const candidates = winnerSeats.filter((s) => (pipCounts[s] ?? Number.MAX_SAFE_INTEGER) === lowest);
  if (candidates.length === 1) return candidates[0];

  let seat = blockingSeat !== undefined ? nextSeat(rules, blockingSeat) : previousOpener;
  for (let i = 0; i <= rules.seatCount; i++) {
    if (candidates.includes(seat)) return seat;
    seat = nextSeat(rules, seat);
  }
  return candidates[0];
}

// ---------------------------------------------------------------------------
// Round lifecycle
// ---------------------------------------------------------------------------

let eventSeqCounter = 0;

function makeEvent(partial: Omit<GameEvent, "seq">): GameEvent {
  return { seq: ++eventSeqCounter, ...partial };
}

/** Append events to a state's log (immutably). */
function withEvents(state: RoundState, events: readonly GameEvent[]): RoundState {
  if (events.length === 0) return state;
  return { ...state, events: [...state.events, ...events] };
}

/** Deal a round and emit the opening events (round-started, deal, opener). */
export function startRound(
  rules: RulesSnapshot,
  seed: number,
  roundNumber: number,
  isRoundOne: boolean,
): ApplyResult {
  const { hands, boneyard } = dealTiles(rules, seed);
  const openerSeat = resolveOpener(hands, rules);
  const events: GameEvent[] = [
    makeEvent({ type: "round-started", data: { roundNumber, roundOne: isRoundOne } }),
    makeEvent({
      type: "deal",
      data: { handCounts: hands.map((h) => h.length), seatCount: rules.seatCount },
    }),
    makeEvent({ type: "opener", seat: openerSeat }),
  ];
  const state: RoundState = {
    roundNumber,
    rules,
    hands,
    boneyard,
    chain: emptyChain(),
    currentSeat: openerSeat,
    turnNumber: 1,
    status: "active",
    passStreak: 0,
    redealsUsed: 0,
    seed,
    events,
    openerSeat,
  };
  return { state, events };
}

/**
 * Draw availability: draw modes only, not below the configured floor.
 * House rule (ADR-0016): drawing is FREE — a seat may draw at any time on
 * its turn, matching or not, as many tiles as it likes.
 */
export function canDraw(state: RoundState): boolean {
  return state.rules.boneyard === "draw" && state.boneyard.length > state.rules.boneyardFloor;
}

function finishRound(
  state: RoundState,
  reason: RoundResult["reason"],
  events: readonly GameEvent[],
): ApplyResult {
  const result = computeRoundResult({ ...state, status: "ended", endReason: reason });
  const ending = makeEvent({
    type: "round-ended",
    data: { reason, points: result.points, tied: result.tied },
  });
  const all = [...events, ending];
  const state0 = withEvents({ ...state, status: "ended", endReason: reason }, all);
  return { state: { ...state0, result }, events: all };
}

/**
 * Auto-resolve draws and passes until a seat can act, or the round blocks.
 * Draws emit one event each; drawn identities stay private (counts only).
 */
export function settle(state: RoundState): ApplyResult {
  let s = state;
  const emitted: GameEvent[] = [];
  let guard = 0;

  while (guard++ < 500) {
    if (s.status !== "active") break;
    if (s.chain.placements.length === 0) break; // the opener must play
    const hand = s.hands[s.currentSeat];
    if (hand.length === 0) break;
    if (hasAnyLegalMove(hand, s.chain)) break;

    if (canDraw(s)) {
      const drawn = s.boneyard[0];
      const hands = s.hands.map((h, i) => (i === s.currentSeat ? [...h, drawn] : h));
      s = { ...s, hands, boneyard: s.boneyard.slice(1) };
      emitted.push(makeEvent({ type: "draw", seat: s.currentSeat, boneyardCount: s.boneyard.length }));
      continue;
    }

    const passedSeat = s.currentSeat;
    emitted.push(makeEvent({ type: "pass", seat: passedSeat }));
    s = {
      ...s,
      passStreak: s.passStreak + 1,
      currentSeat: nextSeat(s.rules, passedSeat),
      turnNumber: s.turnNumber + 1,
    };

    const canDrawMore =
      s.rules.boneyard === "draw" &&
      s.boneyard.length > s.rules.boneyardFloor;

    if (s.passStreak >= s.rules.seatCount && !canDrawMore) {
      const blocked = finishRound(s, "block", [...emitted, makeEvent({ type: "block" })]);
      return blocked;
    }
  }

  return { state: withEvents(s, emitted), events: emitted };
}

/** Play a tile for the current seat, then settle the table. */
export function applyPlay(state: RoundState, seat: number, tileId: number, end?: End): ApplyResult {
  if (state.status !== "active") throw new Error("Round is not active");
  if (seat !== state.currentSeat) throw new Error("Not your turn");

  const hand = state.hands[seat];
  if (!hand.includes(tileId)) throw new Error("Tile not in hand");

  // ADR-0019: the opening play must LEAD the highest double — every round,
  // not just round one. Enforced here, not only in the projection.
  if (state.chain.placements.length === 0 && state.rules.openerMustPlayHighestDouble) {
    const required = highestDoubleIn(hand);
    if (required !== null && tileId !== required) {
      throw new Error("The opener must lead the highest double");
    }
  }

  const legalEnds = listLegalEnds(state.chain, tileId);
  if (legalEnds.length === 0) throw new Error("Tile does not match an open end");

  let chosen: End;
  if (state.chain.placements.length === 0) {
    if (end !== undefined && end !== "center") {
      throw new Error("The opening tile is played with end 'center'");
    }
    chosen = "center";
  } else if (legalEnds.length === 1) {
    if (end !== undefined && end !== legalEnds[0]) throw new Error("Tile does not match that end");
    chosen = legalEnds[0];
  } else {
    if (end === undefined || end === "center") throw new Error("End required but missing");
    if (!legalEnds.includes(end)) throw new Error("Tile does not match that end");
    chosen = end;
  }

  const chain = applyPlayToChain(state.chain, tileId, chosen);
  const hands = state.hands.map((h, i) => (i === seat ? h.filter((t) => t !== tileId) : h));
  const emitted: GameEvent[] = [makeEvent({ type: "play", seat, tileId, end: chosen })];

  const advanced: RoundState = {
    ...state,
    chain,
    hands,
    passStreak: 0,
    currentSeat: nextSeat(state.rules, seat),
    turnNumber: state.turnNumber + 1,
  };

  if (hands[seat].length === 0) {
    return finishRound(advanced, "domino", [...emitted, makeEvent({ type: "domino", seat })]);
  }

  return { state: withEvents(advanced, emitted), events: emitted };
}

/**
 * Manual pass (ADR-0016, amended by ADR-0019): allowed at any time after the
 * opening tile — even when a legal move exists — BUT a stuck seat (no legal
 * move) must draw while the boneyard still has tiles. The opener must play.
 */
export function applyPass(state: RoundState, seat: number): ApplyResult {
  if (state.status !== "active") throw new Error("Round is not active");
  if (seat !== state.currentSeat) throw new Error("Not your turn");
  if (state.chain.placements.length === 0) {
    throw new Error("Pass not allowed (the opener must play)");
  }
  if (!hasAnyLegalMove(state.hands[seat], state.chain) && canDraw(state)) {
    throw new Error("Draw first: no legal move and the boneyard still has tiles");
  }

  const events: GameEvent[] = [makeEvent({ type: "pass", seat })];
  const advanced: RoundState = {
    ...state,
    passStreak: state.passStreak + 1,
    currentSeat: nextSeat(state.rules, seat),
    turnNumber: state.turnNumber + 1,
  };
  const canDrawMore =
    advanced.rules.boneyard === "draw" &&
    advanced.boneyard.length > advanced.rules.boneyardFloor;

  if (advanced.passStreak >= advanced.rules.seatCount && !canDrawMore) {
    return finishRound(advanced, "block", [...events, makeEvent({ type: "block" })]);
  }
  return { state: withEvents(advanced, events), events };
}

/** Draw one tile for the current seat (draw modes). */
export function applyDraw(state: RoundState, seat: number): ApplyResult {
  if (state.status !== "active") throw new Error("Round is not active");
  if (seat !== state.currentSeat) throw new Error("Not your turn");
  if (!canDraw(state)) throw new Error("Draw not allowed");

  const drawn = state.boneyard[0];
  const hands = state.hands.map((h, i) => (i === seat ? [...h, drawn] : h));
  const events: GameEvent[] = [
    makeEvent({ type: "draw", seat, boneyardCount: state.boneyard.length - 1 }),
  ];
  const advanced: RoundState = {
    ...state,
    hands,
    boneyard: state.boneyard.slice(1),
  };
  return { state: withEvents(advanced, events), events };
}

// ---------------------------------------------------------------------------
// Scoring (§2.9)
// ---------------------------------------------------------------------------

/** Side index of a seat: team id in 2v2, seat index otherwise. */
export function sideOfSeat(rules: RulesSnapshot, seat: number): number {
  return teamForSeat(rules.mode, seat) ?? seat;
}

export function pipCount(hand: readonly number[]): number {
  return hand.reduce((sum, id) => sum + pipValueOf(id), 0);
}

/** Remaining pips for one side. */
export function sidePips(state: RoundState, side: number): number {
  return seatsForSide(state.rules.mode, side).reduce(
    (sum, seat) => sum + pipCount(state.hands[seat] ?? []),
    0,
  );
}

/** Pip count per side (boneyard tiles are never counted). */
export function sidePipCounts(state: RoundState): number[] {
  const totals = new Array(sideCount(state.rules)).fill(0) as number[];
  state.hands.forEach((hand, seat) => {
    totals[sideOfSeat(state.rules, seat)] += pipCount(hand);
  });
  return totals;
}

/** Compute the round result from the final hands and nothing else. */
export function computeRoundResult(state: RoundState): RoundResult {
  const rules = state.rules;
  const pipCounts: Record<string, number> = {};
  state.hands.forEach((hand, seat) => {
    pipCounts[String(seat)] = pipCount(hand);
  });
  const sides = sidePipCounts(state);
  const bonuses: BonusLine[] = [];

  if (state.endReason === "domino") {
    const winnerSeat = state.hands.findIndex((h) => h.length === 0);
    const side = sideOfSeat(rules, winnerSeat);
    let points = opposingSides(rules.mode, side).reduce(
      (sum, other) => sum + sidePips(state, other),
      0,
    );
    // ADR-0019 (2v2): the losing team takes ALL pips left on the table —
    // the winners' side too (the domino player is empty; their partner may
    // still hold tiles, and those count against the losers).
    if (rules.mode === "2v2") points += sidePips(state, side);
    if (rules.bonuses.doubleOut) {
      const last = state.chain.placements[state.chain.placements.length - 1];
      if (last && getTile(last.tileId).isDouble) {
        bonuses.push({ key: "double-out", points: 10, seat: winnerSeat });
        points += 10;
      }
    }
    return {
      reason: "domino",
      winners: [winnerSeat],
      winningSide: side,
      points,
      pipCounts,
      bonuses,
      tied: false,
    };
  }

  // Block: the lowest pip count wins; a tie scores nothing and rotates the opener.
  const min = Math.min(...sides);
  const winningSides = sides.map((v, i) => (v === min ? i : -1)).filter((i) => i >= 0);
  if (winningSides.length !== 1) {
    return {
      reason: "block",
      winners: [],
      winningSide: null,
      points: 0,
      pipCounts,
      bonuses: [],
      tied: true,
    };
  }

  const side = winningSides[0];
  const opponentsTotal = opposingSides(rules.mode, side).reduce((sum, s) => sum + sides[s], 0);
  let points = rules.blockScoring === "difference" ? Math.max(0, opponentsTotal - sides[side]) : opponentsTotal;
  // ADR-0019 (2v2): a blocked qafla still hands every hand's pips to the
  // losing team — winning side's remaining tiles included.
  if (rules.mode === "2v2") points = sides.reduce((sum, v) => sum + v, 0);
  if (rules.bonuses.blocker) {
    bonuses.push({ key: "blocker", points: 5, seat: seatsForSide(rules.mode, side)[0] });
    points += 5;
  }
  return {
    reason: "block",
    winners: seatsForSide(rules.mode, side),
    winningSide: side,
    points,
    pipCounts,
    bonuses,
    tied: false,
  };
}

// ---------------------------------------------------------------------------
// Match (§2.10)
// ---------------------------------------------------------------------------

export interface MatchProgress {
  mode: RulesSnapshot["mode"];
  seatCount: number;
  scores: number[];
  targetScore: number;
  roundNumber: number;
  status: "lobby" | "active" | "finished";
  winner: number | null;
  carryOver: number;
}

export function newMatch(rules: RulesSnapshot): MatchProgress {
  return {
    mode: rules.mode,
    seatCount: rules.seatCount,
    scores: new Array(sideCount(rules)).fill(0) as number[],
    targetScore: rules.targetScore,
    roundNumber: 1,
    status: "active",
    winner: null,
    carryOver: 0,
  };
}

/**
 * Apply a round result using Egyptian Domino penalty scoring:
 * The loser(s) receive penalty points equal to the unplayed tiles.
 * First side to reach or exceed the target score (default 100) loses.
 */
export function applyRoundResultToMatch(match: MatchProgress, result: RoundResult): MatchProgress {
  const scores = [...match.scores];
  let carryOver = match.carryOver;

  if (result.winningSide === null) {
    carryOver = match.carryOver; // tied block: no score
  } else {
    // The loser(s) accumulate penalty points
    const opponents = opposingSides(match.mode, result.winningSide);
    if (match.mode === "3p") {
      opponents.forEach((side) => {
        scores[side] += result.points + carryOver;
      });
    } else {
      const losingSide = opponents[0];
      if (losingSide !== undefined) {
        scores[losingSide] += result.points + carryOver;
      }
    }
    carryOver = 0;
  }

  // First to reach or exceed targetScore loses
  const loser = scores.findIndex((s) => s >= match.targetScore);
  let winner: number | null = null;
  let status: "active" | "finished" = "active";

  if (loser >= 0) {
    status = "finished";
    let lowestScore = Infinity;
    scores.forEach((s, side) => {
      if (s < lowestScore) {
        lowestScore = s;
        winner = side;
      }
    });
  }

  return {
    ...match,
    scores,
    carryOver,
    roundNumber: match.roundNumber + 1,
    status,
    winner,
  };
}

// ---------------------------------------------------------------------------
// Projection (the only path to clients; §5.1/§6.2)
// ---------------------------------------------------------------------------

export function projectToPlayerView(state: RoundState, mySeat: number): PlayerView {
  const myHand = state.hands[mySeat] ?? [];
  const opening = state.chain.placements.length === 0;
  const legalMoves = opening
    ? openingLegalMoves(myHand, state.rules, state.roundNumber === 1)
    : listLegalMoves(myHand, state.chain);
  const isMyTurn = state.status === "active" && state.currentSeat === mySeat;
  return {
    roundNumber: state.roundNumber,
    status: state.status,
    mySeat,
    myHand: myHand.map(getTile),
    handCounts: state.hands.map((h) => h.length),
    boneyardCount: state.boneyard.length,
    chain: state.chain,
    currentSeat: state.currentSeat,
    turnNumber: state.turnNumber,
    legalMoves,
    canDraw: isMyTurn && canDraw(state),
    canPass: isMyTurn && !opening && (legalMoves.length > 0 || !canDraw(state)),
    events: state.events,
    ...(state.result ? { result: state.result } : {}),
  };
}

/** Spectators never see hands during play. */
export type SpectatorView = Omit<PlayerView, "mySeat" | "myHand" | "legalMoves">;

export function projectToSpectatorView(state: RoundState): SpectatorView {
  return {
    roundNumber: state.roundNumber,
    status: state.status,
    handCounts: state.hands.map((h) => h.length),
    boneyardCount: state.boneyard.length,
    chain: state.chain,
    currentSeat: state.currentSeat,
    turnNumber: state.turnNumber,
    events: state.events,
    ...(state.result ? { result: state.result } : {}),
  };
}

export interface RevealView {
  perSeatTiles: number[][];
  perSeatPipCounts: number[];
  result?: RoundResult;
}

/** Post-round reveal: all final hands plus pip counts. */
export function projectToRevealView(state: RoundState): RevealView {
  return {
    perSeatTiles: state.hands.map((h) => [...h]),
    perSeatPipCounts: state.hands.map(pipCount),
    ...(state.result ? { result: state.result } : {}),
  };
}

// ---------------------------------------------------------------------------
// Local simulation (no bots package): heaviest-first policy, deterministic.
// Used for smoke tests and local hot-seat play.
// ---------------------------------------------------------------------------

/** Pick the heaviest legal move for the current seat, or null. */
export function pickSimpleMove(state: RoundState): { tileId: number; end: End } | null {
  const hand = state.hands[state.currentSeat];
  if (!hand || hand.length === 0) return null;
  const moves =
    state.chain.placements.length === 0
      ? openingLegalMoves(hand, state.rules, state.roundNumber === 1)
      : listLegalMoves(hand, state.chain);
  if (moves.length === 0) return null;
  const move = [...moves].sort((a, b) => pipValueOf(b.tileId) - pipValueOf(a.tileId))[0];
  return { tileId: move.tileId, end: move.ends[0] };
}

/** Play a round to completion deterministically (heaviest-first). */
export function simulateRound(rules: RulesSnapshot, seed: number, roundNumber = 1): RoundState {
  let state = startRound(rules, seed, roundNumber, roundNumber === 1).state;
  let guard = 0;
  while (state.status === "active" && guard++ < 2000) {
    const move = pickSimpleMove(state);
    if (move) {
      state = applyPlay(state, state.currentSeat, move.tileId, move.end).state;
      continue;
    }
    if (canDraw(state)) {
      state = applyDraw(state, state.currentSeat).state;
      continue;
    }
    state = applyPass(state, state.currentSeat).state;
  }
  return state;
}

export { shuffle };
